/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IObservation } from '@ahryman40k/ts-fhir-types/lib/R4';
import { forwardRef, useMemo, useRef } from 'react';
import styles from './CurrentVitalsContainer.module.scss';
import { observationValueToText } from './utils';
import FHIRAccordion from '../Fhir/FHIRAccordion';
import { DocumentVersionType, fhirPatientDataTypes } from '../algorithm/types';
import clsx from 'clsx';

const vitalNameByLoincCode: Record<string, string> = {
  '8867-4': 'Pulse',
  '8480-6': 'Systolic blood pressure',
  '8462-4': 'Diastolic blood pressure',
  '29463-7': 'Weight',
  '8302-2': 'Height',
  '39156-5': 'BMI (calculated)',
};

function getVitalName(code: IObservation['code']) {
  const loincCode = code?.coding?.find((coding) => coding.system === 'http://loinc.org')?.code;
  return (loincCode && vitalNameByLoincCode[loincCode]) || code?.text || code?.coding?.[0]?.display;
}

/** Displays the current vitals in a collapsible accordion
 *  The documentVersion is only for analytics.  FIXME: If you have to do this, use a context instead.
 *
 *  Gets the vitals from the fhirSlice
 */
export function CurrentVitalsContainer({
  documentVersion,
  vitals: vitals_,
}: {
  documentVersion?: DocumentVersionType;
  vitals: IObservation[];
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const vitals = useMemo(() => {
    // Vitals can be duplicated, so just take the latest vital.
    // Also vitals can have components, so we need to flatten them.
    const map = new Map<string, IObservation>();
    let newestVitalDate: string | undefined = undefined;
    vitals_?.forEach((v) => {
      if (v.component) {
        v.component.forEach((component) => {
          const name = getVitalName(component.code);
          const componentObservation = {
            ...component,
            resourceType: 'Observation' as const,
            status: v.status,
            effectiveDateTime: v.effectiveDateTime,
          } as IObservation;
          if (
            name &&
            (!map.has(name) ||
              (map.get(name)?.effectiveDateTime || '') < (componentObservation.effectiveDateTime || ''))
          ) {
            map.set(name, componentObservation);
            if (
              componentObservation.effectiveDateTime &&
              (!newestVitalDate || componentObservation.effectiveDateTime > newestVitalDate)
            ) {
              newestVitalDate = componentObservation.effectiveDateTime;
            }
          }
        });
      } else {
        const name = getVitalName(v.code);
        if (name && (!map.has(name) || (map.get(name)?.effectiveDateTime || '') < (v.effectiveDateTime || ''))) {
          map.set(name, v);
          if (v.effectiveDateTime && (!newestVitalDate || v.effectiveDateTime > newestVitalDate)) {
            newestVitalDate = v.effectiveDateTime;
          }
        }
      }
    });
    if (map.size === 0) {
      return undefined;
    }
    return { map, newestVitalDate };
  }, [vitals_]);

  if (!vitals) {
    return null;
  }
  const fhirDataType = fhirPatientDataTypes.find((x) => x.id === 'Vitals');

  return (
    <FHIRAccordion
      heading={fhirDataType?.value}
      subHeading={
        vitals.newestVitalDate
          ? new Date(vitals.newestVitalDate)
              .toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
              .replace(',', '')
          : undefined
      }
      divRef={divRef}
      mixpanelWidgetName="vital_signs"
      documentVersion={documentVersion}
      style={{ minWidth: vitals.newestVitalDate ? 140 : 110 }}
    >
      <VitalsWidget ref={divRef} vitals={vitals.map} />
    </FHIRAccordion>
  );
}

interface IVitalsWidget {
  vitals: Map<string, IObservation>;
}

/** Displays the vitals as a table, in a standard format */
const VitalsWidget = forwardRef<HTMLDivElement, IVitalsWidget>(({ vitals }, ref) => {
  function o(name: string, expectedUnits?: string): string {
    const v = vitals.get(name);
    if (v && name === 'Temp' && v.valueQuantity?.unit === 'Cel') {
      // Handle temperature specially - check if unit is "Cel" and convert to F if so
      const cel = v.valueQuantity.value;
      if (cel === undefined) {
        return '--';
      }
      const fahr = cel * (9 / 5) + 32;
      return `${cel}°C (${fahr.toFixed(1)}°F)`;
    }
    const value = observationValueToText(v, expectedUnits);
    return (name === 'BMI (calculated)' ? value?.replace('kg/m2', 'kg/m²') : value) || '--';
  }
  return (
    <div ref={ref} className={styles.tableContainer}>
      <table className={clsx(styles.table, styles.topTable)}>
        <tbody>
          <tr>
            <th>BP</th>
            <th>HR</th>
            <th>Temp</th>
            <th>RR</th>
            <th>SpO2</th>
          </tr>
          <tr>
            <td>
              {o('Systolic blood pressure', 'mm[Hg]')}/{o('Diastolic blood pressure', 'mm[Hg]')}
            </td>
            <td>{o('Pulse', '/min')}</td>
            <td>{o('Temp')}</td>
            <td>{o('Resp', '/min')}</td>
            <td>{o('SpO2')}</td>
          </tr>
        </tbody>
      </table>
      <table className={styles.table}>
        <tbody>
          <tr>
            <th>Weight</th>
            <th>Height</th>
            <th>BMI</th>
          </tr>
          <tr>
            <td>{o('Weight')}</td>
            <td>{o('Height')}</td>
            <td>{o('BMI (calculated)')}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});
